import React, { useState } from "react";
import { LoadingButton } from "@mui/lab";
import DownloadTwoToneIcon from "@mui/icons-material/DownloadTwoTone";
import moment from "moment";
import Excel from "exceljs";
import { saveAs } from "file-saver";
import { generateQueryParams } from "nhb-toolbox";
import { httpService } from "src/Helpers/utils/axios";
import { toast } from "src/Toaster/Toaster";
import type { IB2B_list } from "../type/b2bType";

interface Props {
	searchParams?: { name?: string; type?: string };
	dateRange?: { start_date: string; end_date: string };
	totalCount?: number;
	currentData?: IB2B_list[];
	isLoading?: boolean;
	fileName?: string;
	getDownloadData?: () => Promise<{ data: any }>;
}

const formatStatus = (status?: string): string => {
	if (!status) return "-";
	const map: Record<string, string> = {
		pending: "Pending",
		approved: "Approved",
		rejected: "Rejected",
		active: "Active",
	};
	return (
		map[status.toLowerCase()] ||
		status.charAt(0).toUpperCase() + status.slice(1)
	);
};

const B2BReportExcelGeneration: React.FC<Props> = ({
	searchParams,
	dateRange,
	totalCount,
	currentData,
	isLoading,
	fileName = "B2B_List",
	getDownloadData,
}) => {
	const [isExporting, setIsExporting] = useState<boolean>(false);

	const handleExportExcel = async () => {
		try {
			setIsExporting(true);

			let b2bList: IB2B_list[] = [];

			if (getDownloadData) {
				const response = await getDownloadData();
				b2bList = Array.isArray(response?.data)
					? response.data
					: Array.isArray(response)
						? response
						: [];
			} else if (
				currentData &&
				totalCount !== undefined &&
				currentData.length >= totalCount &&
				currentData.length > 0
			) {
				b2bList = currentData;
			} else {
				const queryParams = generateQueryParams({
					limit: totalCount && totalCount > 0 ? totalCount : 5000,
					skip: 0,
					name: searchParams?.name?.trim() || undefined,
					type: searchParams?.type || undefined,
					dateRange:
						dateRange?.start_date || dateRange?.end_date
							? dateRange
							: undefined,
				});

				const res = await httpService.get<IB2B_list[]>({
					url: `/api/fair/admin/b2b${queryParams}`,
				});

				b2bList = Array.isArray(res?.data) ? res.data : [];
			}

			if (!b2bList || b2bList.length === 0) {
				toast.fire({
					icon: "warning",
					title: "No B2B data available to export!",
				});
				return;
			}

			const workbook = new Excel.Workbook();
			const worksheet = workbook.addWorksheet(fileName || "B2B List");

			// Define columns
			worksheet.columns = [
				{ header: "SL", key: "sl", width: 8 },
				{ header: "Full Name", key: "full_name", width: 24 },
				{ header: "First Name", key: "first_name", width: 18 },
				{ header: "Last Name", key: "last_name", width: 18 },
				{ header: "Designation", key: "designation", width: 22 },
				{ header: "Organization", key: "organization", width: 30 },
				{ header: "B2B Type", key: "b2b_type", width: 16 },
				{ header: "Phone", key: "phone", width: 20 },
				{ header: "Email", key: "email", width: 28 },
				{ header: "Status", key: "status", width: 16 }
			];

			// Style header row
			const headerRow = worksheet.getRow(1);
			headerRow.font = {
				bold: true,
				color: { argb: "FFFFFFFF" },
				size: 11,
			};
			headerRow.fill = {
				type: "pattern",
				pattern: "solid",
				fgColor: { argb: "FF2E7D32" }, // Forest Green theme matching TOAB branding
			};
			headerRow.alignment = { vertical: "middle", horizontal: "center" };
			headerRow.height = 28;

			// Add data rows
			b2bList.forEach((item, index) => {
				const fullName =
					[item.b2b_first_name, item.b2b_last_name]
						.filter(Boolean)
						.join(" ")
						.trim() || "-";

				const createdAt = (item as any)?.b2b_created_at
					? moment((item as any).b2b_created_at).isValid()
						? moment((item as any).b2b_created_at).format(
							"YYYY-MM-DD"
						)
						: String((item as any).b2b_created_at)
					: "-";

				const row = worksheet.addRow({
					sl: index + 1,
					full_name: fullName,
					first_name: item.b2b_first_name || "-",
					last_name: item.b2b_last_name || "-",
					designation: item.b2b_designation || "-",
					organization: item.b2b_organization || "-",
					b2b_type: item.b2b_type || "-",
					phone: item.b2b_phone || "-",
					email: item.b2b_email || "-",
					status: formatStatus(item.b2b_status)
				});

				row.alignment = { vertical: "middle" };
				row.height = 22;

				// Center align key identifier cells
				row.getCell("sl").alignment = {
					vertical: "middle",
					horizontal: "center",
				};
				row.getCell("b2b_type").alignment = {
					vertical: "middle",
					horizontal: "center",
				};
				row.getCell("phone").alignment = {
					vertical: "middle",
					horizontal: "center",
				};
				row.getCell("status").alignment = {
					vertical: "middle",
					horizontal: "center",
				};
			});

			// Add cell borders
			worksheet.eachRow((row) => {
				row.eachCell((cell) => {
					cell.border = {
						top: { style: "thin", color: { argb: "FFE0E0E0" } },
						left: { style: "thin", color: { argb: "FFE0E0E0" } },
						bottom: { style: "thin", color: { argb: "FFE0E0E0" } },
						right: { style: "thin", color: { argb: "FFE0E0E0" } },
					};
				});
			});

			const buffer = await workbook.xlsx.writeBuffer();
			saveAs(
				new Blob([buffer]),
				`${fileName}_${moment().format("YYYY-MM-DD")}.xlsx`
			);

			toast.fire({
				icon: "success",
				title: "B2B list exported successfully!",
			});
		} catch (error: any) {
			console.error("Failed to export Excel:", error);
			toast.fire({
				icon: "error",
				title: error?.message || "Failed to export Excel",
			});
		} finally {
			setIsExporting(false);
		}
	};

	return (
		<LoadingButton
			variant="outlined"
			size="small"
			loading={isExporting}
			disabled={isLoading || totalCount === 0}
			startIcon={<DownloadTwoToneIcon />}
			onClick={handleExportExcel}
			sx={{
				borderRadius: 1,
				textTransform: "none",
				fontWeight: 600,
				fontSize: "0.8125rem",
				px: 1.8,
				py: 0.7,
				whiteSpace: "nowrap",
			}}
		>
			Export Excel
		</LoadingButton>
	);
};

export default B2BReportExcelGeneration;
